import React, {
    forwardRef,
    MutableRefObject,
    useCallback,
    useEffect,
    useState,
    useImperativeHandle,
    useRef
} from 'react';
import {LexicalEditor} from 'lexical';
import {LexicalComposer} from '@lexical/react/LexicalComposer';
import {LexicalErrorBoundary} from '@lexical/react/LexicalErrorBoundary';
import {$generateHtmlFromNodes} from '@lexical/html';

import {
    RichTextPlugin,
    DragDropPaste,
    HistoryPlugin,
    TreeViewPlugin,
    LinkPlugin,
    FocusPlugin,
    AutoFocusPlugin,
    ImagesPlugin,
    ListMaxIndentLevelPlugin,
    AutoLinkPlugin,
    HorizontalRulePlugin,
    ListPlugin,
    TablePlugin,
    ContentEditable
} from '../../../../../components/LexicalEditor/plugins';
import {Placeholder} from '../../../../../components/LexicalEditor/components/Placeholder';
import {getEditorConfig} from '../../../../../components/LexicalEditor/editorConfig';
import {DirectionPlugin} from '../../../../../components/LexicalEditor/plugins/DirectionPlugin';
import {ExposeEditorInstancePlugin} from '../../../../../components/LexicalEditor/plugins/ExposeEditorInstancePlugin';
import {EmojisPlugin} from '../../../../../components/LexicalEditor/plugins/EmojisPlugin';
import {HtmlPlugin} from './plugins/HTMLPlugin/HtmlPlugin';
import {ToolbarPlugin} from './plugins/ToolbarPlugin/ToolbarPlugin';
import {isRTL} from '../../../../../Localization';
import {TollbarTextsContext} from '../../../../../components/LexicalEditor/components/Toolbar.parts/Toolbar/TollbarText.context';
import '../../../../../components/LexicalEditor/editor-theme.scss';
import {texts} from './texts';
import {TOAST_ID} from './constants';
import {AILottieLoader} from '../../../../../components/AILoader/AILottieLoader';
import ToastModal from '../../../../../components/Toast/component/Toast';
import styles from '../styles.module.scss';
import {ThemeUpdater} from '../../../../../components/LexicalEditor/plugins/ThemeUpdater';
import {StyledParagraphNode} from '../../../../../components/LexicalEditor/nodes/StyledParagrah';
import {ParagraphReplacementPlugin} from '../../../../../components/LexicalEditor/plugins/ParagraphReplacementPlugin';

type Props = {
    onSave: () => void;
    value: string;
    placeholder?: string;
    isReadonly?: boolean;
    onFocus?: () => void;
    autofocus?: boolean;
    saveDisabled?: boolean;
    namespace: string;
    onUnmount?: (html: string) => void;
    formID?: string;
    session: string;
};

export type ExposedEditorData = {
    editor: MutableRefObject<LexicalEditor>;
};

const params = new URLSearchParams(window.location.search);
const debugHtml = Number(params.get('_lexical-editor-debug'));
const rtl = isRTL();

export const Editor = forwardRef((props: Props, ref) => {
    const {
        onFocus,
        onSave,
        value = '',
        placeholder,
        isReadonly,
        namespace,
        onUnmount,
        autofocus,
        session,
        saveDisabled,
        formID
    } = props;
    const editorConfig = useRef(getEditorConfig(namespace, [StyledParagraphNode]));
    const editorRef = useRef<LexicalEditor>();
    const editorDivRef = useRef<HTMLDivElement>(null);
    const onUnmountCallbackRef = useRef(onUnmount);
    const toastRef = useRef(null);

    const showSuccessToast = (message: string) => {
        if (toastRef.current) {
            toastRef.current.showToast(message, 'success', {
                containerId: TOAST_ID,
                position: isRTL() ? 'bottom-left' : 'bottom-right',
                className: 'editor-toast-success'
            });
        }
    };

    const [AILoading, setAILoading] = useState(false);

    const setEditorInstance = useCallback(editor => {
        editorRef.current = editor;
    }, []);

    useImperativeHandle(
        ref,
        (): ExposedEditorData => ({
            editor: editorRef
        }),
        []
    );

    useEffect(() => {
        const editorInstance = editorRef.current;

        return function onUnmount() {
            editorInstance?.update(() => {
                const html = $generateHtmlFromNodes(editorInstance);
                onUnmountCallbackRef.current?.(html);
            });
        };
    }, []);

    return (
        <LexicalComposer initialConfig={editorConfig.current}>
            <div className="priority-editor" id={formID}>
                <TollbarTextsContext.Provider value={texts}>
                    <ToolbarPlugin
                        rtl={rtl}
                        formID={formID}
                        onSave={onSave}
                        toggleAILoading={setAILoading}
                        disabled={AILoading}
                        showSuccessToast={showSuccessToast}
                        session={session}
                        saveDisabled={saveDisabled}
                    />
                </TollbarTextsContext.Provider>

                <div
                    className="priority-editor__inner"
                    dir={isRTL() ? 'rtl' : 'ltr'}
                    style={AILoading ? {overflow: 'hidden'} : {}}
                >
                    {AILoading ? (
                        <div className="priority-editor__ai-loader">
                            <AILottieLoader />
                        </div>
                    ) : null}

                    <RichTextPlugin
                        contentEditable={
                            <ContentEditable
                                ref={editorDivRef}
                                contentEditable={!isReadonly}
                                className="priority-editor__content"
                            />
                        }
                        placeholder={<Placeholder text={placeholder} />}
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    <ExposeEditorInstancePlugin callback={setEditorInstance} />
                    <DragDropPaste />
                    <HistoryPlugin />
                    <TablePlugin cellEditorConfig={editorConfig.current} />
                    <ListPlugin />
                    <LinkPlugin />
                    <ImagesPlugin />
                    <HorizontalRulePlugin />
                    <AutoLinkPlugin />
                    <DirectionPlugin />
                    <ListMaxIndentLevelPlugin maxDepth={7} />
                    <EmojisPlugin />

                    {autofocus && <AutoFocusPlugin defaultSelection={'rootStart'} />}
                    <FocusPlugin onFocus={onFocus} />

                    <HtmlPlugin initialHtml={value} />
                    <ThemeUpdater isRtl={rtl} />
                    <ParagraphReplacementPlugin />

                    {debugHtml ? <TreeViewPlugin /> : null}
                    <ToastModal
                        ref={toastRef}
                        containerId={TOAST_ID}
                        componentClasses={styles.toastContainer}
                    />
                </div>
            </div>
        </LexicalComposer>
    );
});
